import { usePersistedState } from "@/lib/persistedFilters";
// Iter 91ak — Global + per-app changelog viewer.
//
// Two tabs:
//   • Global — all app releases merged, newest first
//   • By app — pick an app from a dropdown, see just its history
//
// Iter 122 — super admins can edit (version, kind, date, released-by,
// notes) or delete any entry straight from the feed.

import { useEffect, useMemo, useState } from "react";
import { Sparkles, GitCommit, Filter, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const KIND_STYLES = {
  patch: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
  minor: "bg-primary/15 text-primary",
  major: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300",
};

export default function Changelog() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = usePersistedState("changelog.tab", "global");
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [globalItems, setGlobalItems] = useState([]);
  const [perAppItems, setPerAppItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("changelog.q", "");
  const [kindFilter, setKindFilter] = usePersistedState("changelog.kind", "all");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/apps").then((r) => setApps((r.data || []).sort((a, b) => a.name.localeCompare(b.name))))
       .catch(() => {});
  }, []);

  const loadGlobal = () => {
    setLoading(true);
    api.get("/changelog", { params: { limit: 200 } })
      .then((r) => setGlobalItems(r.data.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  const loadPerApp = () => {
    if (!selectedApp) { setPerAppItems([]); return; }
    api.get(`/apps/${selectedApp}/changelog`, { params: { limit: 200 } })
      .then((r) => setPerAppItems(r.data.items || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(loadGlobal, []);
  useEffect(loadPerApp, [selectedApp]);

  const refresh = () => { loadGlobal(); loadPerApp(); };

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.app_name} v${e.version} from the changelog? This cannot be undone.`)) return;
    try {
      await api.delete(`/changelog/${e.entry_id}`);
      toast.success(`Deleted ${e.app_name} v${e.version}`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete entry");
    }
  };

  const filteredGlobal = useMemo(() => {
    const lc = q.trim().toLowerCase();
    return globalItems.filter((e) => {
      if (kindFilter !== "all" && e.kind !== kindFilter) return false;
      if (!lc) return true;
      return e.app_name?.toLowerCase().includes(lc) ||
             e.notes?.toLowerCase().includes(lc) ||
             e.version?.toLowerCase().includes(lc);
    });
  }, [globalItems, q, kindFilter]);

  return (
    <div className="space-y-4" data-testid="changelog-page">
      <PageHeader
        icon={<Sparkles className="h-5 w-5 text-primary" />}
        title="Changelog"
        subtitle="Every release across every app."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent gap-4 border-b border-slate-100 dark:border-slate-800 rounded-none w-full justify-start">
          <TabsTrigger value="global" data-testid="changelog-tab-global" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
            Global feed
          </TabsTrigger>
          <TabsTrigger value="perapp" data-testid="changelog-tab-perapp" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
            By app
          </TabsTrigger>
        </TabsList>

        <TabsContent value="global" className="mt-4">
          <Card className="p-4 border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="flex-1 min-w-[200px]">
                <Input data-testid="changelog-search" placeholder="Search app, notes, version…"
                       value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Filter className="h-3.5 w-3.5" />
                {["all", "patch", "minor", "major"].map((k) => (
                  <button key={k} data-testid={`changelog-filter-${k}`}
                          onClick={() => setKindFilter(k)}
                          className={`rounded px-2 py-1 font-medium capitalize ${kindFilter === k ? "bg-primary/15 text-primary" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <FeedList items={filteredGlobal} loading={loading} isSuper={isSuper}
                      onEdit={setEditing} onDelete={remove}
                      emptyMsg="No releases yet — bump an app's version to see it here." />
          </Card>
        </TabsContent>

        <TabsContent value="perapp" className="mt-4">
          <Card className="p-4 border-slate-200 dark:border-slate-700">
            <div className="mb-4 max-w-md">
              <Select value={selectedApp} onValueChange={setSelectedApp}>
                <SelectTrigger data-testid="changelog-app-select">
                  <SelectValue placeholder="Pick an app to view its history…" />
                </SelectTrigger>
                <SelectContent>
                  {apps.map((a) => (
                    <SelectItem key={a.app_id} value={a.app_id} data-testid={`changelog-app-option-${a.app_id}`}>
                      {a.name} {a.version ? `— v${a.version}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedApp ? (
              <FeedList items={perAppItems} loading={false} isSuper={isSuper}
                        onEdit={setEditing} onDelete={remove}
                        emptyMsg="This app hasn't been bumped yet — its first release entry will appear here." />
            ) : (
              <div className="p-6 text-center text-sm text-slate-400">Pick an app above to load its release history.</div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <EditEntryDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
    </div>
  );
}

function FeedList({ items, loading, emptyMsg, isSuper, onEdit, onDelete }) {
  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Loading…</div>;
  if (!items.length) return <div className="p-8 text-center text-sm text-slate-400" data-testid="changelog-empty">{emptyMsg}</div>;
  return (
    <ol className="relative ml-3 border-l border-slate-200 dark:border-slate-700">
      {items.map((e) => (
        <li key={e.entry_id} data-testid={`changelog-entry-${e.entry_id}`} className="group mb-6 ml-6">
          <span className="absolute -left-2 mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-white dark:bg-slate-900 ring-2 ring-slate-200 dark:ring-slate-700">
            <GitCommit className="h-2.5 w-2.5 text-slate-500 dark:text-slate-400" />
          </span>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{e.app_name}</span>
            <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[11px] text-slate-700 dark:text-slate-200">v{e.version}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_STYLES[e.kind] || "bg-slate-100 text-slate-600"}`}>{e.kind}</span>
            <time className="ml-auto text-xs text-slate-400" dateTime={e.released_at}>
              {new Date(e.released_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </time>
            {isSuper && (
              <span className="flex items-center gap-1 opacity-60 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => onEdit(e)} title="Edit this entry"
                        data-testid={`changelog-edit-${e.entry_id}`}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => onDelete(e)} title="Delete this entry"
                        data-testid={`changelog-delete-${e.entry_id}`}
                        className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{e.notes}</p>
          <div className="mt-1 text-xs text-slate-400">Released by {e.released_by_name || "—"}</div>
        </li>
      ))}
    </ol>
  );
}

function EditEntryDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) { setForm(null); return; }
    setForm({
      version: entry.version || "",
      kind: entry.kind || "patch",
      released_at: (entry.released_at || "").slice(0, 10),
      released_by_name: entry.released_by_name || "",
      notes: entry.notes || "",
    });
  }, [entry]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/changelog/${entry.entry_id}`, form);
      toast.success("Changelog entry updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="changelog-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Edit release entry</DialogTitle>
          <DialogDescription className="text-xs">
            {entry?.app_name} — changing the newest entry's version also updates the app's current version.
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Version</Label>
                <Input data-testid="changelog-edit-version" value={form.version}
                       onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="2.0.0" />
              </div>
              <div className="space-y-1.5">
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="changelog-edit-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["patch", "minor", "major"].map((k) => (
                      <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Release date</Label>
                <Input type="date" data-testid="changelog-edit-date" value={form.released_at}
                       onChange={(e) => setForm({ ...form, released_at: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Released by</Label>
                <Input data-testid="changelog-edit-by" value={form.released_by_name}
                       onChange={(e) => setForm({ ...form, released_by_name: e.target.value })}
                       placeholder="UnitedCloud Product" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Release notes</Label>
              <Textarea rows={8} data-testid="changelog-edit-notes" value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="changelog-edit-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
